import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { useInterpret, useSelector } from '@xstate/react'
import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import React, { createContext, ReactNode, useCallback, useContext, useEffect } from 'react'
import { transformIntoCartCustomerInput } from 'transforms/CartCustomerInputTransform'
import { assign, InterpreterFrom, spawn } from 'xstate'
import {
  CartFieldsFragment,
  CartQuery,
  CartQueryVariables,
  ProductType,
  Scalars,
  ShippingMethodsQuery,
  ShippingMethodsQueryVariables
} from 'types/graphql'
import { assertExists, hasValue } from 'utils/assert'
import { config } from 'utils/config'
import { fractionize } from 'utils/currencyTransforms'
import { usePathParam } from 'utils/usePathParam'
import { useUiContext } from 'modules/UiContext'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { shippingMethodMachine } from 'modules/checkout/context/shippingMethodMachine'
import { getShippingMethodsQuery } from 'modules/checkout/queries/shippingMethods.query'
import { getCartQuery } from 'modules/checkout/queries/useCart'
import { useCreateCart } from 'modules/checkout/queries/useCreateCart'
import { useGetPaymentIntentClientSecret } from 'modules/checkout/queries/useGetPaymentIntentClientSecret'
import { useSelectShippingMethods } from 'modules/checkout/queries/useSelectShippingMethods'
import { useUpdateCart } from 'modules/checkout/queries/useUpdateCart'
import { useUpdateCartCustomer } from 'modules/checkout/queries/useUpdateCartCustomer'
import { mapCartProductsToTrackingParams } from '../utils/mapCartProductsToTrackingParams'
import { mapCartSkusToCartSkuUpdateInput } from '../utils/mapCartSkusToCartSkuUpdateInput'
import { cartMachine, CheckoutSteps } from './cartMachine'

const CART_ID_STORAGE_KEY = 'cart_id'

interface Context {
  cartService: InterpreterFrom<typeof cartMachine>
  addVariantToCart: (productVariantId: Scalars['ID']) => void
  removeVariantFromCart: (productVariantId: Scalars['ID']) => void
}

const CartContext = createContext<Context | null>(null)

export const useCartContext = () => {
  const ctx = useContext(CartContext)
  assertExists(ctx, 'useCartContext can only be used within <CartContextProvider>')
  return ctx
}

type ErrorType = { type: 'string'; data?: { cart?: Partial<CartFieldsFragment> } & { message?: string } }

interface Props {
  children: ReactNode
}

export const CartContextProvider = ({ children }: Props) => {
  const cartIdFromPath = usePathParam('cartId')
  const { addNotification } = useUiContext()
  const [updateCart] = useUpdateCart()
  const [createCart] = useCreateCart()
  const [updateCartCustomer] = useUpdateCartCustomer()
  const [selectShippingMethods] = useSelectShippingMethods()
  const [fetchPaymentClientIntentSecret] = useGetPaymentIntentClientSecret()
  const client = useApolloClient()
  const { pathname, push, events: routerEvents } = useRouter()
  const { user } = useCurrentUser()

  const cartService = useInterpret(cartMachine, {
    devTools: config.apiEnv !== 'production',
    actions: {
      setRouterToCustomerForm: (context) => {
        push(`/checkout/${context.cart?.id}#${CheckoutSteps.customer}`, undefined, { shallow: true })
      },
      setRouterToShippingAndPaymentForm: (context) => {
        push(`/checkout/${context.cart?.id}#${CheckoutSteps.shippingAndPayment}`, undefined, { shallow: true })
      },
      setRouterToComplete: (context) => {
        push(`/checkout/${context.cart?.id}#${CheckoutSteps.complete}`, undefined, { shallow: true })
      },

      clearCartIdFromStorage: () => {
        window.sessionStorage.removeItem(CART_ID_STORAGE_KEY)
      },

      trackCheckoutStarted: ({ cart }) => {
        if (cart) {
          window.analytics.track('Checkout Started', {
            order_id: cart.id,
            affiliation: 'Thingtesting',
            value: cart.total ? fractionize(cart.total) : null,
            revenue: cart.subTotal ? fractionize(cart.subTotal) : null,
            shipping: cart.shippingTotal ? fractionize(cart.shippingTotal) : null,
            tax: cart.taxTotal ? fractionize(cart.taxTotal) : null,
            currency: cart.currency,
            products: mapCartProductsToTrackingParams(cart)
          })
        }
      },
      trackCustomerStepViewed: ({ cart }) => {
        if (cart) {
          window.analytics.track('Checkout Step Viewed', {
            checkout_id: cart.id,
            step: 1
          })
        }
      },
      trackCustomerStepCompleted: ({ cart }) => {
        if (cart) {
          window.analytics.track('Checkout Step Completed', {
            checkout_id: cart.id,
            step: 1
          })
        }
      },
      trackPaymentStepViewed: ({ cart }) => {
        if (cart) {
          window.analytics.track('Checkout Step Viewed', {
            checkout_id: cart.id,
            step: 2
          })
        }
      },
      trackOrderCompleted: ({ cart }) => {
        assertExists(cart)
        window.analytics.track('Order Completed', {
          checkout_id: cart.id,
          order_id: cart.id,
          affiliation: 'Thingtesting',
          subtotal: cart.subTotal ? fractionize(cart.subTotal) : null,
          total: cart.total ? fractionize(cart.total) : null,
          revenue: cart.subTotal ? fractionize(cart.subTotal) : null,
          shipping: cart.shippingTotal ? fractionize(cart.shippingTotal) : null,
          tax: cart.taxTotal ? fractionize(cart.taxTotal) : null,
          currency: cart.currency,
          products: mapCartProductsToTrackingParams(cart)
        })
      },
      handleError: (ctx, event, meta) => {
        window.analytics.track('Checkout Failure', { event })

        const error = event as unknown as ErrorType

        if ((error.data?.cart?.errors?.length ?? 0) > 0) {
          addNotification('error', 'Something went wrong')
          return
        }

        switch (error.data?.message) {
          case 'VioletApiException':
            addNotification('error', 'Something went wrong')
            break
          case 'Product is limited to people level 1 or above':
          case 'You can only purchase a Thingdrop once':
            addNotification('error', error.data.message)
            break
          default:
            Sentry.withScope((scope) => {
              scope.setLevel(Sentry.Severity.Error)
              scope.setUser(user ?? null)
              Sentry.captureMessage(`Unknown cart error encountered ${JSON.stringify(event)}`)
            })
        }

        // eslint-disable-next-line no-console
        console.error({ ctx, event, meta })
      },
      registerShippingMethodMachines: assign({
        shippingMethodActors: (context, event) => {
          return event.data.cartShippingMethods.map((shippingMethod) => {
            const bagContainsPhysicalItems =
              context.cart?.bags.find((bag) => bag.id === shippingMethod.bag_id)?.skus.some((sku) => sku.product_type === ProductType.Physical) ??
              true

            const actor = shippingMethodMachine.withContext({
              cartId: context.cart?.id ?? null,
              bagId: shippingMethod.bag_id,
              shippingMethods: shippingMethod.shipping_methods,
              selectedShippingMethodId: context.cart?.bags.find((bag) => bag.id === shippingMethod.bag_id)?.shipping_method?.shipping_method_id,
              containsPhysicalProducts: bagContainsPhysicalItems
            })

            return spawn(actor, { name: `bag-${shippingMethod.bag_id}` })
          })
        }
      })
    },
    guards: {
      notOnCheckoutPage: (_) => !pathname.startsWith('/checkout'),
      onCheckoutPage: (_) => pathname.startsWith('/checkout')
    },
    services: {
      initializeCart: async (_) => {
        // If you load the page from a url, use that cart ID
        if (!!cartIdFromPath) {
          window.sessionStorage.setItem(CART_ID_STORAGE_KEY, cartIdFromPath)
        }

        const cartId = window.sessionStorage.getItem(CART_ID_STORAGE_KEY)

        if (!cartId) {
          return null
        }

        const {
          data: { cart }
        } = await client.query<CartQuery, CartQueryVariables>({ query: getCartQuery, variables: { id: cartId } })

        return cart
      },
      createCart: async (_, event) => {
        const { data } = await createCart({
          variables: {
            productVariants: event.cartUpdateInput.productVariants
          }
        })

        assertExists(data?.createCart, 'no cart returned from create mutation')

        window.sessionStorage.setItem(CART_ID_STORAGE_KEY, data.createCart.id)

        return data.createCart
      },
      updateCart: async (context, event) => {
        assertExists(context.cart, 'no cart in context')

        // If we want to remove the last item from the cart, we should discard the entire cart instead since violet
        // doesn't support a cart without any items in it
        if (Array.isArray(event.cartUpdateInput.productVariants) && event.cartUpdateInput.productVariants.length === 0) {
          window.sessionStorage.removeItem(CART_ID_STORAGE_KEY)
          return null
        }

        const { data } = await updateCart({ variables: { productVariants: event.cartUpdateInput.productVariants, id: context.cart.id } })
        assertExists(data?.updateCart, 'no cart returned from update mutation')

        // The same is true here. If we ever end up with a cart with no items it should be discarded
        if (data.updateCart.bags.length === 0) {
          window.sessionStorage.removeItem(CART_ID_STORAGE_KEY)
          return null
        }

        return data.updateCart
      },
      saveCustomer: async (context, event) => {
        assertExists(context.cart)

        const existing = transformIntoCartCustomerInput({
          ...context.cart.customer,
          ...context.cart.shippingAddress
        })

        if (!event.saveCustomerInput) {
          if (!context.cart.customer || !context.cart.shippingAddress) {
            throw new Error('No customer input was provided and no customer info was in context')
          }

          return context.cart
        }

        // If the form was submitted, but nothing changed, skip sending anything to the api and just return the current cart
        if (isEqual(event.saveCustomerInput, existing)) {
          return context.cart
        }

        const { data } = await updateCartCustomer({ variables: { id: context.cart.id, input: event.saveCustomerInput } })

        assertExists(data?.updateCartCustomer)

        const cart = data.updateCartCustomer

        if (cart.errors.length > 0) {
          return cart
        }

        return cart
      },
      getShippingMethods: async (context) => {
        assertExists(context.cart)

        const { data } = await client.query<ShippingMethodsQuery, ShippingMethodsQueryVariables>({
          query: getShippingMethodsQuery,
          variables: { id: context.cart.id }
        })

        assertExists(data.cartShippingMethods)

        return data
      },
      selectShippingMethods: async (context, event) => {
        assertExists(context.cart)

        const shippingMethodsWithNonPhysicalProducts = Object.entries({ ...context.shippingMethods, [event.bagId]: event.shippingMethodId }).map(
          ([bagId, shippingMethodId]) => ({
            bag_id: +bagId,
            shipping_method_id: shippingMethodId as string | null
          })
        )

        // We need to remove the selection of shipping method for each bag that doesn't contain a physical product
        const shippingMethods = shippingMethodsWithNonPhysicalProducts.filter((shippingMethod) => shippingMethod.shipping_method_id !== null)

        const { data } = await selectShippingMethods({ variables: { cartId: context.cart.id, shippingMethods } })

        assertExists(data?.selectShippingMethodsAndGetPaymentIntentSecret)

        return data.selectShippingMethodsAndGetPaymentIntentSecret
      },
      getPaymentIntentClientSecret: async (context) => {
        assertExists(context.cart)

        const { data } = await fetchPaymentClientIntentSecret({ variables: { cartId: context.cart.id } })

        assertExists(data?.getPaymentIntentClientSecret)

        return data?.getPaymentIntentClientSecret
      }
    }
  })

  const cart = useSelector(cartService, (state) => state.context.cart)
  const addVariantToCart = useCallback(
    (productVariantId: Scalars['ID']) => {
      const existingProductVariants =
        cart?.bags.flatMap((bag) =>
          bag.skus
            .map((sku) => (hasValue(sku.productVariant) ? { productVariantId: sku.productVariant.id, quantity: sku.quantity } : null))
            .filter(hasValue)
        ) ?? []

      cartService.send({ type: 'UPDATE_CART', cartUpdateInput: { productVariants: [...existingProductVariants, { productVariantId, quantity: 1 }] } })
    },
    [cartService, cart]
  )

  const removeVariantFromCart = useCallback(
    (productVariantId: Scalars['ID']) => {
      assertExists(cart)
      const productVariants = mapCartSkusToCartSkuUpdateInput(cart).filter((item) => item.productVariantId !== productVariantId)
      cartService.send({ type: 'UPDATE_CART', cartUpdateInput: { productVariants } })
    },
    [cart, cartService]
  )

  /**
   * Listen to popstate event (back/forward browser buttons) and try to transition to
   * the correct state when it changes.
   */
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      try {
        const stepKey = e.state.as.split('#')[1] as keyof typeof CheckoutSteps
        const step = CheckoutSteps[stepKey]
        switch (step) {
          case CheckoutSteps.customer:
            cartService.send({ type: 'EDIT_CUSTOMER' })
            break

          case CheckoutSteps.shippingAndPayment:
            cartService.send({ type: 'SAVE_CUSTOMER' })
            break
        }
      } catch {}
    }
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [cartService])

  // Make sure that the cart gets reset after each completed transaction.
  // This makes sure the user can complete multipe checkouts in the same session
  // The machine should always be in the idle state when navigating away from the checkout page
  const inCompleteState = useSelector(cartService, (state) => state.matches('checkout.complete'))
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (inCompleteState) {
        cartService.send('RESET_CART')
        return
      }

      if (!pathname.startsWith('/checkout')) {
        return
      }

      if (!url.startsWith('/checkout')) {
        cartService.send('RETURN_TO_IDLE')
      }
    }

    routerEvents.on('routeChangeStart', handleRouteChange)

    return () => {
      routerEvents.off('routeChangeStart', handleRouteChange)
    }
  }, [routerEvents, inCompleteState, cartService, pathname])

  return <CartContext.Provider value={{ cartService, addVariantToCart, removeVariantFromCart }}>{children}</CartContext.Provider>
}
